import React, { useEffect, useState } from "react";
import { FormControl, Select, MenuItem} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./company-dashboard-filter.scss";
import { usePermissions } from "../../../common/hooks/usePermissions";
import MODULES from "../../../constants/modules";
import { useDispatch } from "react-redux";
import DepartmentStatusMultiSelect from "../../../common/components/DepartmentStatusMultiSelect/DepartmentStatusMultiSelect";
import { setDashboardTab } from "../../../reducers/dashboard";
import { Add } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";

const CompanyDashboardFilter: any = (props: any) => {
  const {
    objective,
    status,
    objectiveYear,
    initiativeYear,
    setObjective,
    onObjectiveYearChange,
    onInitiativeYearChange,
    handleAddObjective,
    handleAddInitiative,
    statusList,
    setStatus,
    departmentsList,
    departments, 
    setDepartments,
    handleShowAllObjectiveDrawer,
    handleShowAddInitiativeDrawer
  } = props;
  const dispatch = useDispatch();

  // Internal state for selected years
  const [selectedObjectiveYear, setSelectedObjectiveYear] = useState("");
  const [selectedInitiativeYear, setSelectedInitiativeYear] = useState("");
  // Set first option as default when year options are loaded
  useEffect(() => {
    if (objectiveYear.length > 0) {
      setSelectedObjectiveYear(objectiveYear[0]);
      if (onObjectiveYearChange) {
        onObjectiveYearChange(objectiveYear[0]);
      }
    }
  }, [objectiveYear]);

  useEffect(() => {
    if (initiativeYear.length > 0) {
      setSelectedInitiativeYear(initiativeYear[0]);
      if (onInitiativeYearChange) {
        onInitiativeYearChange(initiativeYear[0]);
      }
    }
  }, [initiativeYear]);

  const { canRead: canReadObjectives, canWrite: canWriteObjectives } =
    usePermissions(MODULES.OBJECTIVES);

  const { canRead: canReadInitiatives, canWrite: canWriteInitiatives } =
    usePermissions(MODULES.INITIATIVES);

  if (!canReadObjectives && !canReadInitiatives) {
    return null;
  }

  const handleObjectChange = (event: any) => {
    setObjective(event.target.value);
    setDepartments([]); // Reset department filter when switching tabs
    setStatus([]); // Reset status filter when switching tabs 
    handleShowAllObjectiveDrawer(false);
    handleShowAddInitiativeDrawer(false);
    dispatch(setDashboardTab(event.target.value));
  };
  const handleObjectiveYearChange = (event: any) => {
    const selectedYear = event.target.value;
    setSelectedObjectiveYear(selectedYear);
    // Notify parent component about the selected year
    if (onObjectiveYearChange) {
      onObjectiveYearChange(selectedYear);
    }
  };
  const handleInitiativeYearChange = (event: any) => {
    const selectedYear = event.target.value;
    setSelectedInitiativeYear(selectedYear);
    // Notify parent component about the selected year
    if (onInitiativeYearChange) {
      onInitiativeYearChange(selectedYear);
    }
  };

  const yearOptions =
    objective === "Objectives" ? objectiveYear : initiativeYear;

  return (
    <div className="company-dashboard-filter">
      <div className="company-dashboard-filter-content">
        <div className="company-dashboard-filter-content-right">
          <div className="company-dashboard-filter-content-right-item">
            <div className="company-dashboard-filter-content-right-item-label">
              Views
            </div>

            <FormControl
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "30px", // rounded pill
                  backgroundColor: "#fff",
                  "& fieldset": {
                    border: "none", // remove border
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                },
              }}
            >
              <Select
                value={objective}
                onChange={handleObjectChange}
                IconComponent={ExpandMoreIcon}
                displayEmpty
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#403C6F",
                  paddingX: "15px",
                  paddingY: "8px",
                  "& .MuiSelect-icon": {
                    color: "#403C6F",
                    right: "8px",
                  },
                }}
                MenuProps={{
                  slotProps: {
                    paper: {
                      sx: {
                        backgroundColor: "#6E6D92",
                        borderRadius: "12px",
                        color: "#FFFFFF",
                        "& .MuiMenuItem-root": {
                          "&.Mui-selected": {
                            backgroundColor: "#403C6F",
                          },
                          "&:hover": {
                            backgroundColor: "#403C6F",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {<MenuItem value="Objectives">Objectives</MenuItem>}{" "}
                {canReadInitiatives && (
                  <MenuItem value="Initiatives">Initiatives</MenuItem>
                )}{" "}
                <MenuItem
                  value="Upcoming Milestones"
                  className="company-dashboard-filter-content-right-item-label-mobile"
                >
                  Upcoming Milestones
                </MenuItem>{" "}
              </Select>
            </FormControl>
          </div>
          <div className="company-dashboard-filter-content-right-item">
            <div className="company-dashboard-filter-content-right-item-label">
              Status
            </div>
            <DepartmentStatusMultiSelect
              label="Status"
              options={statusList?.map((statusItem: any) => statusItem?.statusName)}
              value={status}
              onChange={setStatus}
            />
          </div>
          <div className="company-dashboard-filter-content-right-item">
            <div className="company-dashboard-filter-content-right-item-label">
              Department
            </div>
            <DepartmentStatusMultiSelect
              label="Department"
              options={departmentsList?.map((dept: any) => dept?.departmentName)}
              value={departments}
              onChange={setDepartments}
            />
          </div>
        </div>
        <div className="company-dashboard-filter-content-left">
          <div className="company-dashboard-filter-content-left-item">
            <div className="company-dashboard-filter-content-left-item-action-button">
              {objective === "Objectives"
                ? canWriteObjectives && (
                    <button
                      className="company-dashboard-filter-content-left-item-action-button-btn"
                      onClick={handleAddObjective}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Add fontSize="small" /> Add Objective
                    </button>
                  )
                : canWriteInitiatives && (
                    <button
                      className="company-dashboard-filter-content-left-item-action-button-btn"
                      onClick={handleAddInitiative}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Add fontSize="small" /> Add Initiative
                    </button>
                  )}
            </div>
            <FormControl
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "30px", // rounded pill
                  backgroundColor: "#fff",
                  "& fieldset": {
                    border: "none", // remove border
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-disabled": {
                    cursor: "not-allowed !important",
                  },
                },
              }}
            >
              <Tooltip
                title={
                  yearOptions?.length === 0
                    ? "No data available for any year."
                    : ""
                }
                placement="top"
                disableHoverListener={yearOptions?.length !== 0}
              >
                <span style={{ display: "block" }}>
                  <Select
                    value={
                      objective === "Objectives"
                        ? selectedObjectiveYear
                        : selectedInitiativeYear
                    }
                    IconComponent={ExpandMoreIcon}
                    displayEmpty
                    disabled={yearOptions?.length === 0}
                    onChange={
                      objective === "Objectives"
                        ? handleObjectiveYearChange
                        : handleInitiativeYearChange
                    }
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#403C6F",
                      paddingX: "15px",
                      paddingY: "8px",
                      "& .MuiSelect-icon": {
                        color: "#403C6F",
                        right: "8px",
                      },
                      "&.Mui-disabled": {
                        opacity: 1,
                        cursor: "not-allowed !important",
                        backgroundColor: "#fff",
                        color: "#403C6F",
                        pointerEvents: "all !important",
                        "& .MuiSelect-icon": {
                          opacity: 0.4,
                          cursor: "not-allowed !important",
                        },
                        "& .MuiSelect-select": {
                          cursor: "not-allowed !important",
                        },
                        WebkitTextFillColor: "#403C6F",
                      },
                    }}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: {
                            backgroundColor: "#6E6D92",
                            borderRadius: "12px",
                            color: "#FFFFFF",
                            "& .MuiMenuItem-root": {
                              "&.Mui-selected": {
                                backgroundColor: "#403C6F",
                              },
                              "&:hover": {
                                backgroundColor: "#403C6F",
                              },
                            },
                          },
                        },
                      },
                    }}
                  >
                    {yearOptions?.map((year: any) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </Tooltip>
            </FormControl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboardFilter;
